<!--
  RIGHE DELL'AGENDA — l'elenco a tratte, condiviso da Agenda e Home.

  ⚠️ UNA RIGA È UNA TRATTA, non una prenotazione (vedi `agendaRows.js`).

  Slot `azioni` (facoltativo): se c'è, a destra compare una colonna in più.
  Un click dentro quella colonna NON apre la riga.
-->
<template>
  <div
    class="agr"
    :class="{ 'agr--azioni': $slots.azioni }"
  >
    <div class="agr__row agr__row--head">
      <span class="agr__th">Quando</span>
      <span class="agr__th">Servizio</span>
      <span class="agr__th">Auto e cliente</span>
      <span class="agr__th">Percorso</span>
      <span class="agr__th">Stato</span>
      <span
        v-if="$slots.azioni"
        class="agr__th"
      >Azioni</span>
    </div>

    <div
      v-for="r in righe"
      :key="r.key"
      class="agr__row"
      :class="{ 'is-clickable': apribile(r) }"
      @click="apri(r)"
    >
      <!-- quando: data sopra, orari sotto -->
      <div>
        <div class="agr__date">
          {{ giorno(r.start || r.date) }}
        </div>
        <div class="agr__times">
          <span class="agr__tl">Part</span>
          <span class="agr__tt">{{ ora(r.start) }}</span>
        </div>
        <div class="agr__times">
          <span class="agr__tl">Arr</span>
          <span class="agr__tt agr__tt--arr">{{ ora(r.end) }}</span>
        </div>
      </div>

      <!-- servizio + qualificatori -->
      <div>
        <ServiceChip :service="r.service" />
        <div class="agr__quals">
          <span
            v-if="r.direction"
            class="agr__qual"
          >{{ r.direction === 'gone' ? 'Andata' : 'Ritorno' }}</span>
          <span
            v-if="r.operators"
            class="agr__op"
            :class="r.operators === 2 ? 'agr__op--2' : 'agr__op--1'"
          >{{ r.operators === 2 ? 'Singolo' : 'A catena' }}</span>
          <!-- Una corsa a cui mancano dati è indistinguibile da una pronta:
               qui lo si dice, e il titolo elenca cosa manca. -->
          <span
            v-if="r.mancanti && r.mancanti.length"
            class="agr__todo"
            :title="`Mancano: ${r.mancanti.join(', ')}`"
          >Da completare</span>
        </div>
      </div>

      <!-- auto (principale) e cliente -->
      <div>
        <div class="agr__plate">
          {{ r.plate || '—' }}
        </div>
        <div class="agr__meta">
          {{ r.carLabel }}
        </div>
        <div
          v-if="r.loanerPlate"
          class="agr__cort"
        >
          <div class="agr__cortLab">
            Auto di cortesia
          </div>
          <div class="agr__cortPlate">
            {{ r.loanerPlate }}
          </div>
        </div>
        <template v-if="r.customer">
          <div class="agr__sep" />
          <div class="agr__cust">
            {{ r.customer }}
          </div>
          <div class="agr__meta">
            {{ r.phone }} <span class="agr__id">#{{ r.shortId }}</span>
          </div>
        </template>
      </div>

      <!-- percorso: sempre due capi -->
      <div>
        <div class="agr__pt">
          <span class="agr__pin agr__pin--from" />
          <span class="agr__addr">{{ r.from || '—' }}</span>
        </div>
        <div class="agr__pt">
          <span class="agr__pin agr__pin--to" />
          <span class="agr__addr">{{ r.to || '—' }}</span>
        </div>
        <div
          v-if="r.km"
          class="agr__meta agr__km"
        >
          {{ km(r.km) }}
        </div>
      </div>

      <div>
        <StatusPill
          :kind="r.stateKind"
          :label="r.stateLabel"
        />
      </div>

      <div
        v-if="$slots.azioni"
        class="agr__azioni"
        @click.stop
      >
        <slot
          name="azioni"
          :riga="r"
        />
      </div>
    </div>

    <p
      v-if="!righe.length && !loading"
      class="agr__empty"
    >
      {{ vuoto }}
    </p>
  </div>
</template>

<script>
import ServiceChip from '@/Components/ui/ServiceChip.vue';
import StatusPill from '@/Components/ui/StatusPill.vue';

export default {
  name: 'AgendaRighe',
  components: { ServiceChip, StatusPill },
  props: {
    righe: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    vuoto: { type: String, default: 'Niente in agenda per questo periodo.' },
    // Una riga si segnala come cliccabile SOLO se il click la porta davvero da
    // qualche parte: il cursore a mano su una riga che non si apre è una
    // promessa non mantenuta.
    apribile: { type: Function, default: () => false },
  },
  emits: ['apri'],
  methods: {
    apri(r) {
      if (this.apribile(r)) this.$emit('apri', r);
    },
    ora(v) {
      return v ? new Date(v).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '—';
    },
    giorno(v) {
      return v ? new Date(v).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
    },
    km(n) {
      // Due decimali: i km vengono dal calcolo su strada e il prezzo si
      // costruisce su quelli, quindi si mostrano come sono.
      return `${Number(n).toFixed(2).replace('.', ',')} km`;
    },
  },
};
</script>

<style scoped>
.agr { background: #fff; border: 1px solid var(--color-border); border-radius: 6px; box-shadow: var(--el-box-shadow-lighter); overflow: hidden; }
/* Il PERCORSO non prende più tutto lo spazio che avanza: su uno schermo largo
   si allargava fino a spingere lo STATO contro il bordo destro, lontano dalla
   riga che descrive. Ora ha un tetto, e lo spazio in eccesso resta in fondo. */
.agr__row { display: grid; grid-template-columns: 132px 186px 224px minmax(0, 380px) 170px; align-items: start; gap: 18px; padding: 14px 20px; border-bottom: 1px solid var(--color-bg-tertiary); }
.agr--azioni .agr__row { grid-template-columns: 132px 186px 224px minmax(0, 380px) 170px 160px; }
.agr__row:last-child { border-bottom: 0; }
.agr__row.is-clickable { cursor: pointer; }
.agr__row.is-clickable:hover { background: var(--color-bg-secondary); }
.agr__row--head { background: var(--color-bg-secondary); border-bottom: 1px solid var(--color-border); padding: 10px 20px; align-items: center; }
.agr__th { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--color-text-secondary); }

.agr__date { font-size: 11.5px; font-weight: 650; color: var(--color-text-secondary); margin-bottom: 5px; }
.agr__times { display: flex; align-items: baseline; gap: 7px; }
.agr__tl { width: 26px; font-size: 9.5px; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase; color: var(--color-text-muted); }
.agr__tt { font-weight: 700; font-size: 14px; font-variant-numeric: tabular-nums; }
.agr__tt--arr { font-weight: 600; color: var(--color-text-secondary); }

.agr__quals { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; margin-top: 6px; }
.agr__qual { font-size: 11px; font-weight: 650; color: var(--state-muted); background: var(--state-muted-bg); border-radius: 20px; padding: 1px 8px; }
/* Il tipo di spostamento si distingue anche per FORMA, non solo per colore:
   contorno = un operatore, pieno = due. Famiglia neutra calda, non usata da
   nessun servizio né da nessuno stato. */
.agr__op { font-size: 11px; font-weight: 650; border-radius: 20px; padding: 1px 9px; }
.agr__op--1 { background: #fff; border: 1px solid #d6d3d1; color: #57534e; }
.agr__op--2 { background: #57534e; border: 1px solid #57534e; color: #fafaf9; }
/* «Da completare»: è un promemoria, non uno stato della corsa — sta fra i
   qualificatori e prende i colori dell'attenzione, non quelli di un servizio. */
.agr__todo { font-size: 11px; font-weight: 650; color: var(--state-attn); background: var(--state-attn-bg); border: 1px solid var(--state-attn-line); border-radius: 20px; padding: 1px 8px; cursor: help; }

.agr__plate { font-size: 14px; font-weight: 700; font-variant-numeric: tabular-nums; }
.agr__meta { font-size: 12px; color: var(--color-text-muted); }
.agr__cort { margin-top: 7px; padding-left: 9px; border-left: 2px solid var(--color-border); }
.agr__cortLab { font-size: 9.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-muted); }
.agr__cortPlate { font-size: 13px; font-weight: 650; font-variant-numeric: tabular-nums; }
.agr__sep { height: 1px; background: var(--color-bg-tertiary); margin: 8px 0 7px; }
.agr__cust { font-size: 13px; font-weight: 600; }
.agr__id { font-variant-numeric: tabular-nums; color: var(--color-brand); }

.agr__pt { display: flex; align-items: flex-start; gap: 8px; }
.agr__pin { width: 8px; height: 8px; border-radius: 50%; margin-top: 5px; flex: none; }
.agr__pin--from { background: var(--color-text-muted); }
.agr__pin--to { background: var(--color-brand); }
.agr__addr { font-size: 12.5px; line-height: 1.45; }
.agr__km { padding-left: 16px; margin-top: 2px; }

.agr__azioni { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; cursor: default; }
.agr__empty { padding: 28px 20px; text-align: center; color: var(--color-text-muted); font-size: 13px; }
</style>
